<template>
  <div class="flex">
    <div>
      <div
        class="h-14 px-6 sticky z-40 left-0 top-24 flex items-center justify-between bg-slate-300"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <div class="flex items-center gap-2">
          <BlSelectBox
            v-if="enableBranch"
            class="max-w-48"
            type="branch"
            v-model="selectedBranchId"
            :options="branchOptions"
            search
            @change="selectedBranchId = $event"
          />
          <div class="flex-shrink-0">
            <BlTextBody2 :text="masterDate" />
          </div>
        </div>
        <div class="flex items-center gap-2">
          <div>
            <BlSelectBox
              v-model="timeslotView"
              :options="timeslotViewOptions"
              dense
              @change="$emit('set-timeslot-view', $event)"
            />
          </div>
          <BlInputButton
            type="outlined"
            dense
            label="Jump to"
            @clicked="jumpToOpened = true"
          />
          <BlInputButton
            type="outlined"
            dense
            label="Today"
            @clicked="onDayClick({ date: null })"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-left"
            @clicked="onNextClick(-1)"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-right"
            @clicked="onNextClick(1)"
          />
        </div>
      </div>
      <div>
        <div class="h-12 flex sticky z-20 top-38 bg-slate-200">
          <div
            class="h-12 w-20 sticky z-10 left-0 flex-shrink-0 bg-slate-200 border-b border-slate-400"
          ></div>
          <div
            v-for="(n, idx) in headers"
            :key="idx"
            class="h-12 py-1 px-2 flex-shrink-0 flex items-center justify-center border-b border-slate-400"
            :style="{ width: `calc(${slotWidth})` }"
          >
            <div
              v-if="enableBranch && n.branch"
              class="h-7 px-2 flex items-center justify-center rounded truncate"
              :style="{ backgroundColor: n.branch.background_color || 'unset' }"
              :title="`${n.label} - ${n.branch.name}`"
            >
              <BlTextBody2
                class="whitespace-no-wrap truncate"
                :text="n.label"
                :style="{ color: n.branch.color || 'unset' }"
              />
            </div>
            <div v-else class="h-7 truncate px-4" :title="n.label">
              <BlTextBody2 :text="n.label" class="truncate" />
            </div>
          </div>
        </div>
        <div class="flex flex-col relative bg-slate-200">
          <div
            v-for="time in clockRound"
            :key="time"
            class="flex"
            :id="`recurring_time_view_${time}`"
          >
            <div
              class="w-20 py-2 px-3 flex-shrink-0 sticky z-10 left-0 bg-slate-200 flex justify-end border-b border-r border-slate-400 transition-all duration-300"
              :style="{ height: `${slotHeight * 2}rem` }"
            >
              <BlTextBody2 :text="time" />
            </div>
            <div
              v-for="(n, idx) in headers"
              :key="idx"
              class="flex-shrink-0 flex flex-col gap-2 items-center justify-center border-b border-r border-slate-400 bg-slate-200 transition-all duration-300"
              :style="{
                height: `${slotHeight * 2}rem`,
                width: `calc(${slotWidth})`
              }"
            ></div>
          </div>
          <div v-if="!timeslotLoading">
            <AvailableTimeslot
              v-for="(item, idx) in filteredTimeslots"
              :key="idx"
              :isCompact="timeslotView === 'compact'"
              :item="item"
              :headers="headers"
              :slotHeight="slotHeight"
              :slotWidth="slotWidth"
              :selectedSlots="selectedSlots"
              @add-slot="onAddSlot"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="fixed z-50 right-6 bottom-6" :style="{ width: '31.25rem' }">
      <div
        class="h-14 px-6 flex items-center gap-4 rounded-t-3xl bg-light-purple-300 cursor-pointer"
        @click="chevronOpened = !chevronOpened"
      >
        <BlIcon
          icon="chevron-down"
          color="secondary"
          class="w-5 h-5 transition-all duration-300"
          :style="{ rotate: !chevronOpened ? '180deg' : '' }"
        />
        <BlTextSubtitle2
          text="Reschedule to a new timeslot"
          class="text-pure-white"
        />
      </div>

      <div
        v-if="chevronOpened"
        class="bg-pure-white overflow-y-auto"
        :style="{ maxHeight: 'calc(100vh - 16rem)' }"
      >
        <div
          v-if="selectedSlots.length !== currentSlotCount"
          class="py-4 px-6 flex flex-col items-center gap-4 text-center bg-pure-white border border-t-0 border-slate-400"
        >
          <div v-if="packageItemInputData">
            <BlTextBody2 text="This client has" />
            <BlTextBody2
              v-if="
                packageItemInputData.total_remaining_reschedule_credit === null
              "
              :text="`unlimited reschedule credit(s)`"
              class="font-bold"
            />
            <BlTextBody2
              v-else
              :text="
                `${packageItemInputData.total_remaining_reschedule_credit} reschedule credit(s)`
              "
              class="font-bold"
            />
            <BlTextBody2
              text="available. To reschedule, please choose a timeslot with a duration of"
            />
            <BlTextBody2
              :text="`${currentSlotCount * 30} minutes.`"
              class="font-bold"
            />
          </div>
          <div class="w-25 flex-shrink-0 rounded shadow-md">
            <div
              class="h-7 flex items-center justify-center rounded-t bg-orange-200"
            ></div>
            <div
              class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
            >
              <BlIcon icon="sad" color="orange-6" class="w-8 h-8" />
            </div>
          </div>
        </div>

        <div v-if="selectedSlots.length === currentSlotCount">
          <div
            class="py-4 px-6 flex flex-col gap-4 bg-pure-white border border-t-0 border-slate-400"
          >
            <div class="flex items-center gap-3">
              <div
                v-if="selectedAvailability"
                class="w-25 flex-shrink-0 rounded shadow-md"
              >
                <div
                  class="h-7 flex items-center justify-center rounded-t bg-orange-200"
                >
                  <BlTextBody2
                    :text="displayDate(selectedAvailability.date, 'dddd')"
                    class="text-orange-700"
                  />
                </div>
                <div
                  class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
                >
                  <BlTextTitle4
                    :text="displayDate(selectedAvailability.date, 'DD')"
                    class="text-orange-700"
                  />
                  <BlTextBody2
                    :text="displayDate(selectedAvailability.date, 'MMMM')"
                    class="text-orange-600"
                  />
                </div>
              </div>
              <div class="w-80 py-3 px-3 flex flex-col gap-3">
                <div
                  class="flex items-center gap-2"
                  :title="
                    therapistData.name || 'The therapist is not yet assigned'
                  "
                >
                  <BlIcon icon="user" color="slate-6" class="w-6 h-6" />
                  <BlTextBody2
                    :text="
                      therapistData.name || 'The therapist is not yet assigned'
                    "
                    class="truncate"
                    :class="{ 'italic ': !therapistData.name }"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <BlIcon
                    icon="clock-outline"
                    color="slate-6"
                    class="w-6 h-6"
                  />
                  <BlTextBody2
                    :text="
                      `${selectedTime.start_time_string} - ${selectedTime.end_time_string}`
                    "
                  />
                </div>
                <div class="flex items-center gap-2">
                  <BlIcon icon="piggy" color="slate-6" class="w-6 h-6" />
                  <BlTextBody2
                    :text="
                      `${selectedSlots.length} credit(s) used for this schedule`
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="isOpenRoom"
            class="py-4 px-6 flex flex-col gap-4 bg-pure-white border border-t-0 border-slate-400"
          >
            <div class="flex flex-col gap-1">
              <BlTextSubtitle1 text="Select a therapist" />
              <BlTextBody2 text="Select the therapist for this client." />
            </div>
            <div class="flex flex-col gap-2">
              <BlSelectBox
                :key="
                  `select-reschedule-therapist-date=${selectedDate}&start_time=${selectedTime.start_time_string}&end_time=${selectedTime.end_time_string}&room_id=${selectedAvailability.room.id}`
                "
                search
                prefixIcon="user"
                v-model="therapistInput"
                placeholder="Select therapist"
                :options="therapistOptions"
                @change="onChangeTherapist"
                :searching="therapistLoading"
                @search="fetchTherapists"
              />
            </div>
          </div>

          <div
            v-if="therapistInput"
            class="py-4 px-6 flex flex-col gap-4 bg-pure-white border border-t-0 border-slate-400"
          >
            <div class="flex flex-col gap-1">
              <BlTextSubtitle1 text="Select credits for this appointment" />
              <BlTextBody2
                text="Select the source of credits to apply for this appointment."
              />
            </div>
            <div class="flex flex-col gap-2">
              <BlSelectBox
                :key="`select-package-item-${therapistInput}`"
                search
                prefixIcon="piggy"
                v-model="packageItemIdInput"
                :placeholder="creditLoading ? 'Loading ...' : 'Select item'"
                :options="packageItemOptions"
                :disabled="!selectedSlots.length"
                drop="up"
                @change="packageItemIdInput = $event"
              />
            </div>
            <div
              v-if="packageItemIdInput"
              class="py-2 px-3 flex items-center justify-between gap-2 bg-cornflower-200 rounded"
            >
              <BlTextBody2
                text="Remaining credits available for this item"
                class="text-cornflower-800"
              />
              <div
                class="h-5.5 w-11 flex items-center justify-center bg-cornflower-800 rounded"
              >
                <BlTextSubtitle3
                  :text="remainingCredits"
                  class="text-cornflower-200"
                />
              </div>
            </div>
          </div>

          <div
            class="py-4 px-6 flex flex-col gap-4 bg-pure-white border border-t-0 border-slate-400"
          >
            <div class="flex flex-col gap-1">
              <div
                class="flex items-center mb-2 cursor-pointer"
                @click="setAssignSupervisorsInput = !setAssignSupervisorsInput"
              >
                <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
                  <BlInputToggle
                    :checked="setAssignSupervisorsInput"
                    @changed="
                      setAssignSupervisorsInput = !setAssignSupervisorsInput
                    "
                  />
                </div>
                <BlTextSubtitle1 text="Assign supervisor" />
              </div>
              <BlTextBody2
                text="If you assign a supervisor, they will be able to view this appointment on their Sessions page without affecting their appointments and availability slots."
              />
            </div>
            <div v-if="setAssignSupervisorsInput" class="flex">
              <BlSearchBox
                :key="
                  `select-assign-supervisors-${client.id}-${therapistInput}`
                "
                type="user"
                v-model="assignSupervisorsInput"
                placeholder="Select therapist"
                :options="assignSupervisorOptions"
                :searching="assignSupervisorsLoading"
                drop="up"
                @search="fetchSupervisors"
                @change="assignSupervisorsInput = $event"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        class="h-18 px-6 grid grid-cols-2 items-center gap-4 border border-t-0 border-slate-400 rounded-b-3xl bg-pure-white"
      >
        <BlInputButton type="plain" label="Cancel" @clicked="$emit('cancel')" />
        <BlInputButton
          label="Update schedule"
          :disabled="isDisabledSubmit"
          @clicked="onSubmit"
        />
      </div>
    </div>

    <JumpToDateModal
      v-if="jumpToOpened"
      :currentDate="selectedDate"
      dimension="daily"
      @close-modal="jumpToOpened = false"
      @submit="onDayClick({ date: $event })"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import moment from "moment";
import { CLOCK_ROUND, CLOCK_HALF } from "@/plugins/assets.js";

import JumpToDateModal from "../../availabilities/timeslot/JumpToDateModal.vue";
import AvailableTimeslot from "./AvailableTimeslot.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlSearchBox from "../../BlSearchBox.vue";

export default {
  name: "SelectRescheduleTimeslot",
  components: {
    JumpToDateModal,
    AvailableTimeslot,
    //
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlTextBody3,
    BlBreadcrumbs,
    BlIcon,
    BlInputButton,
    BlInputToggle,
    BlSelectBox,
    BlSearchBox
  },
  props: {
    timeslotView: { type: String },
    timeslotViewOptions: { type: Array }
    // $emit('set-timeslot-view')
    // $emit('cancel')
    // $emit('continue')
  },
  data() {
    return {
      jumpToOpened: false,
      chevronOpened: true,
      //
      selectedBranchId: 0,
      selectedDate: "",
      selectedAvailability: null,
      selectedSlots: [],
      timeslotLoading: false,
      availableTimeslots: [],
      //
      therapistInput: 0,
      therapistData: { id: 0, name: "" },
      therapistOptions: [],
      therapistLoading: false,
      //
      creditLoading: false,
      clientCreditsData: null,
      packageItemIdInput: null,
      //
      setAssignSupervisorsInput: false,
      assignSupervisorsInput: [],
      assignSupervisorOptions: [],
      assignSupervisorsLoading: false,
      /** */
      clockRound: CLOCK_ROUND,
      clockHalf: CLOCK_HALF
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "branches",
      "rooms",
      "appointment"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    //
    client() {
      return this.appointment.client;
    },
    branchOptions() {
      const allBranch = {
        value: 0,
        label: "All branches",
        class: "font-bold text-light-purple -mx-1"
      };
      const arr = this.branches.map(i => ({
        value: i.id,
        label: i.name,
        color: i.color,
        backgroundColor: i.background_color
      }));
      const noBranch = {
        value: "no",
        label: "No branch",
        color: "#667085",
        backgroundColor: "#F9FAFB"
      };
      return [allBranch, ...arr, noBranch];
    },
    //
    masterDate() {
      const date = this.selectedDate || "";
      return moment(date).format("dddd, DD MMM YYYY");
    },
    slotHeight() {
      // retun value in rem, 1 slot is 30 minutes
      if (this.timeslotView === "compact") return 6.25 / 2;
      return 15 / 2;
    },
    slotWidth() {
      if (this.headers.length > 8) return "12rem";
      else return `(100vw - 8rem - 5rem - 0.5rem) / ${this.headers.length}`;
    },
    headers() {
      return [...this.rooms]
        .sort((a, b) => {
          if (!this.enableBranch) return true;
          const branchA = a.branch_id ? a.branch : { name: "z" };
          const branchB = b.branch_id ? b.branch : { name: "z" };
          const textA = branchA.name.toUpperCase();
          const textB = branchB.name.toUpperCase();
          return textA < textB ? -1 : textA > textB ? 1 : 0;
        })
        .filter(i => {
          if (!this.enableBranch) return true;
          if (!this.selectedBranchId) return true;
          if (this.selectedBranchId === "no") return !i.branch_id;
          return i.branch_id === this.selectedBranchId;
        })
        .map(i => {
          const branch = i.branch_id ? i.branch : null;
          return { ...i, value: i.id, label: i.name, branch };
        });
    },
    currentSlotCount() {
      if (!this.appointment) return 0;
      const start = this.appointment.start_time_string;
      const end = this.appointment.end_time_string;
      return this.getSlotCount(start, end);
    },
    //
    selectedTime() {
      if (!this.selectedSlots.length) {
        return { start_time_string: "00:00", end_time_string: "00:00" };
      }
      const idx = this.selectedSlots.length - 1;
      const start = this.selectedSlots[0].start_time_string;
      const end = this.selectedSlots[idx].end_time_string;
      return { start_time_string: start, end_time_string: end };
    },
    isOpenRoom() {
      if (!this.selectedAvailability) return false;
      return !this.selectedAvailability.user.id;
    },
    filteredTimeslots() {
      const arr = [...this.availableTimeslots];
      const keyHeaders = this.headers.map(i => i.value);
      return arr.filter(i => keyHeaders.includes(i.key.id));
    },
    //
    timeOptions() {
      return [].map(i => {
        return {
          value: i.start_time_string,
          disabled: i.appointment_id
        };
      });
    },
    packageItemOptions() {
      const nullOption = {
        value: -1,
        label: `No credits available. Please add credits first to create an appointment.`,
        disabled: true
      };
      if (!this.clientCreditsData) return [nullOption];
      const availableItems = this.clientCreditsData.available_items || [];
      if (!availableItems.length) return [nullOption];
      const arr = [];
      availableItems.forEach(i => {
        const creditTransactions = i.credit_transactions || [];
        // const totalAvailableCredits = creditTransactions.reduce(
        //   (sum, i) => (sum += i.total_available_credits),
        //   0
        // );
        // const totalReservedCredits = creditTransactions.reduce(
        //   (sum, i) => (sum += i.total_reserved_credits),
        //   0
        // );
        const totalUnusedCredits = creditTransactions.reduce(
          (sum, i) => (sum += i.total_unused_credits),
          0
        );

        const remaining = totalUnusedCredits;
        const isAvailable = i.is_available;
        const suffix = !isAvailable
          ? "Unavailable for this therapist"
          : `${remaining} credit(s) available`;
        const disabled = !isAvailable || remaining < this.selectedSlots.length;

        arr.push({
          ...i,
          total_unused_credits: totalUnusedCredits,
          value: i.package_item_id,
          label: i.package_item_name,
          suffix,
          disabled
        });
      });
      return arr;
    },
    packageItemInputData() {
      if (!this.packageItemIdInput) return null;
      const found = this.packageItemOptions.find(
        i => i.value === this.packageItemIdInput
      );
      if (!found) return null;
      const arr = found.credit_transactions.map(
        i => i.remaining_reschedule_credit
      );
      const sum = arr.reduce((sum, i) => (sum += i || 0), 0);
      let totalRemainingRescheduleCredit = sum;
      if (arr.includes(null)) {
        totalRemainingRescheduleCredit = null;
      }
      let transactionUsed = found.credit_transactions.find(
        i => i.remaining_reschedule_credit === null
      );
      if (!transactionUsed) {
        transactionUsed = found.credit_transactions.find(
          i => i.remaining_reschedule_credit > 0
        );
      }
      return {
        ...found,
        total_remaining_reschedule_credit: totalRemainingRescheduleCredit,
        transaction_used: transactionUsed
      };
    },
    //
    creditsUsed() {
      return this.selectedSlots.length;
    },
    remainingCredits() {
      if (!this.packageItemInputData) return 0;
      const remaining = this.packageItemInputData.total_unused_credits;
      return remaining - this.creditsUsed;
    },
    //
    isDisabledSubmit() {
      if (!this.therapistInput) return true;
      if (this.selectedSlots.length !== this.currentSlotCount) return true;
      if (!this.packageItemIdInput) return true;
      if (!this.packageItemInputData) return true;
      if (this.packageItemInputData && this.packageItemInputData.disabled) {
        return true;
      }
      if (this.packageItemIdInput && this.remainingCredits < 0) return true;
      return false;
    }
  },
  watch: {
    selectedDate(val, oldVal) {
      if (val !== oldVal) {
        this.fetchTimslots(val);
      }
    },
    selectedAvailability(val, oldVal) {
      if (!val) return;
      if ((val && val.availability_id) === (oldVal && oldVal.availability_id)) {
        return;
      }
      const userId = (val.user && val.user.id) || 0;
      const clientId = (this.client && this.client.id) || 0;
      this.fetchAvailbleCredits(clientId, userId);
    },
    therapistInput: {
      immediate: true,
      async handler(val, oldVal) {
        if (val === oldVal) return;
        const { data } = await this.getUser({ id: val });
        this.therapistData = data || { id: 0, name: "" };
      }
    }
  },
  mounted() {
    this.fetchAvailbleCredits(this.appointment.client_id, 0);

    this.selectedDate = this.appointment.date;
    if (this.appointment.credit_transaction_services.length) {
      this.packageItemIdInput = this.appointment.credit_transaction_services[0].package_item_id;
    }

    if (this.appointment.supervisors.length) {
      this.setAssignSupervisorsInput = true;
      this.assignSupervisorsInput = this.appointment.supervisors.map(i => i.id);
      this.assignSupervisorOptions = this.appointment.supervisors.map(i => {
        return { ...i, value: i.id, label: i.name };
      });
    }

    setTimeout(() => {
      let time = Number(this.appointment.start_time_string.split(":")[0]);
      if (time < 10) time = `0${time}`;
      this.onScrollToTime(`${time}:00`);
    }, 1000);
  },
  methods: {
    ...mapActions(["actionGet", "getUser", "getSupervisors"]),
    displayDate(date, format) {
      return displayDate({ date, format });
    },
    getSlotCount(start = "", end = "") {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onScrollToTime(time) {
      const id = `recurring_time_view_${time}`;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    },
    //
    onDayClick(day) {
      let date = day.date || moment().format("YYYY-MM-DD");
      this.selectedDate = date;
    },
    onNextClick(num) {
      const date = moment(this.selectedDate)
        .add(num, "days")
        .format("YYYY-MM-DD");
      this.onDayClick({ date });
    },
    //
    onChangeTherapist(val) {
      this.therapistInput = val;

      const clientId = (this.client && this.client.id) || 0;
      this.fetchAvailbleCredits(clientId, val);
    },
    //
    onAddSlot(slot) {
      const slots = [...this.selectedSlots];
      const availability = this.availableTimeslots.find(
        i => i.unique === slot.unique
      );

      const newId = availability.availability_id;
      const oldId =
        this.selectedAvailability && this.selectedAvailability.availability_id;
      if (newId !== oldId) {
        this.therapistInput = availability.user.id;
      }

      this.selectedAvailability = availability;

      if (slots.length) {
        const isDiffUnique = slots[0].unique !== slot.unique;
        if (isDiffUnique) {
          this.selectedSlots = [slot];
          return;
        }
      }

      let arr = [...slots].map(i => i.start_time_string);
      const value = slot.start_time_string;
      const clocks = this.clockHalf;
      const options = availability.slots.map(i => {
        return {
          value: i.start_time_string,
          disabled: i.appointment_id
        };
      });

      const remove = arr.includes(value);
      if (remove) {
        arr = arr.filter(i => i !== value);
      } else {
        arr.push(value);
      }
      arr.sort((a, b) => clocks.indexOf(a) - clocks.indexOf(b));
      if (arr.length > 1) {
        const firstIndex = clocks.indexOf(arr[0]);
        const lastIdx = clocks.indexOf(arr[arr.length - 1]);
        const valueIdx = clocks.indexOf(value);
        const currentIndex = lastIdx < valueIdx ? lastIdx : valueIdx;
        arr = clocks.filter(
          (_, idx) => idx >= firstIndex && idx <= currentIndex
        );
      }
      let hasDisabled = false;
      arr.forEach(i => {
        const opt = options.find(o => o.value === i);
        if (!opt) hasDisabled = true;
        else if (opt.disabled) hasDisabled = true;
      });
      if (hasDisabled) {
        arr = [value];
      }

      this.selectedSlots = availability.slots.filter(i =>
        arr.includes(i.start_time_string)
      );
    },
    //
    async fetchTimslots(date) {
      const params = {
        url: `/api/v1/timeslots?client_id=${this.client.id}&appointment_id=${this.appointment.id}&group_by=room&type=daily`
      };
      params.url += `&date=${date}`;
      this.timeslotLoading = true;
      const { success, data } = await this.actionGet(params);
      this.timeslotLoading = false;
      if (!success) return;

      this.availableTimeslots = [];
      const arr = [];

      data.data.forEach(body => {
        const key = body.room;

        body.timeslots.forEach(slot => {
          const unique = `${key.id}_${slot.availability_id}_${date}`;
          const therapist = {
            id: slot.availability.user_id,
            name: slot.availability.user_name
          };
          const room = slot.availability.room;
          const usedDate = moment(this.selectedDate).format("YYYY-MM-DD");

          const lastIndex = arr.findIndex(i => i.unique === unique);

          let addition = {
            is_booked: slot.is_booked,
            appointment_id: slot.appointment_id,
            appointment: slot.appointment
          };

          if (this.appointment.id === slot.appointment_id) {
            addition = {
              is_booked: false,
              appointment_id: null,
              appointment: null
            };
          }

          if (lastIndex > -1) {
            arr[lastIndex].end_time_string = slot.end_time_string;
            arr[lastIndex].slots.push({ ...slot, ...addition, unique });
          } else {
            const res = {
              key,
              unique,
              therapist,
              user: therapist,
              room,
              date: usedDate,
              //
              availability_id: slot.availability_id,
              start_time_string: slot.start_time_string,
              end_time_string: slot.end_time_string,
              primary_color: slot.primary_color,
              secondary_color: slot.secondary_color,
              slots: [{ ...slot, ...addition, unique }]
            };
            arr.push(res);
          }
        });
      });

      this.availableTimeslots = arr;
    },
    async fetchAvailbleCredits(clientId, userId) {
      if (!clientId) return;
      this.clientCreditsData = null;
      const params = {
        url: `/api/v1/clients/${clientId}/credit_transaction_details?appointment_id=${this.appointment.id}`
      };
      if (userId) params.url += `&user_id=${userId}`;

      this.creditLoading = true;
      const { success, data } = await this.actionGet(params);
      this.creditLoading = false;
      if (!success) return;

      this.clientCreditsData = data;
    },
    async fetchTherapists(query) {
      let params = `?sort=alphabetical&status=active&date=${this.selectedDate}&start_time=${this.selectedTime.start_time_string}&end_time=${this.selectedTime.end_time_string}&room_id=${this.selectedAvailability.room.id}`;
      if (this.client) params += `&client_id=${this.client.id}`;
      if (query) params += `&query=${query}`;

      this.therapistLoading = true;
      const { success, data } = await this.getSupervisors({ params });
      this.therapistLoading = false;
      if (!success) return;

      this.therapistOptions = data.map(i => ({
        ...i,
        value: i.id,
        label: i.name,
        disabled: !i.is_available,
        suffix: !i.is_available ? "Unavailable" : ""
      }));
    },
    async fetchSupervisors(query) {
      let params = `?sort=alphabetical&status=active`;
      if (query) params += `&query=${query}`;
      params += `&client_id=${this.client.id}`;

      this.assignSupervisorsLoading = true;
      const { success, data } = await this.getSupervisors({ params });
      this.assignSupervisorsLoading = false;

      if (!success) return;
      this.assignSupervisorOptions = data
        .map(i => ({ ...i, value: i.id, label: i.name }))
        .filter(i => i.id !== this.therapistInput);
    },
    //
    onSubmit() {
      const start = this.selectedTime.start_time_string;
      const end = this.selectedTime.end_time_string;

      const slot = {
        key: `${this.selectedDate}_${start}_${this.selectedAvailability.room.id}`,
        user_id: this.therapistInput,
        user: this.therapistData,
        room_id: this.selectedAvailability.room.id,
        room: this.selectedAvailability.room,
        is_open_room: this.isOpenRoom,
        date: this.selectedDate,
        time: this.selectedSlots,
        start_time_string: start,
        start_time: start,
        end_time_string: end,
        end_time: end,
        supervisor_ids: [],
        package_item_id: this.packageItemIdInput,
        service_data: { ...this.packageItemInputData },
        //
        primary_color: this.selectedAvailability.primary_color,
        secondary_color: this.selectedAvailability.secondary_color
      };

      if (this.setAssignSupervisorsInput) {
        slot.supervisor_ids = this.assignSupervisorsInput;
      }

      this.$emit("continue", slot);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/overlay/SelectRescheduleTimeslot.vue